import { useEffect, useMemo, useState } from 'react';
import { adminApi } from './api';
import MobileMoneySandboxAdmin from './MobileMoneySandboxAdmin';
import PaymentHistory from './PaymentHistory';
import PremiumAccessAudit from './PremiumAccessAudit';

function emptyDraft() {
  return {
    code: 'test_premium_10min',
    label: 'Test 10 minutes',
    durationValue: 10,
    durationUnit: 'minute',
    isTest: true,
    isActive: true,
    displayOrder: 50,
    priceXof: 0,
  };
}

function durationLabel(plan) {
  const value = Number(plan.durationValue);
  if (plan.durationUnit === 'minute') {
    return `${value} minute${value > 1 ? 's' : ''}`;
  }
  return `${value} mois`;
}

export default function SubscriptionPlanManager({ onUnauthorized }) {
  const [state, setState] = useState({
    plans: [],
    mockPaymentsEnabled: false,
    shortTestPlansEnabled: false,
    production: false,
  });
  const [draft, setDraft] = useState(emptyDraft);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyCode, setBusyCode] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  async function load() {
    try {
      setLoading(true);
      setError('');
      const result = await adminApi.billingPlans();
      setState(result);
      setEdits(Object.fromEntries(
        (result.plans || []).map((plan) => [plan.code, { ...plan }])
      ));
    } catch (failure) {
      setError(failure.message || 'Chargement des formules impossible.');
      if (failure.status === 401) onUnauthorized?.();
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orderedPlans = useMemo(
    () => [...(state.plans || [])].sort(
      (a, b) => Number(a.displayOrder) - Number(b.displayOrder)
        || a.code.localeCompare(b.code)
    ),
    [state.plans]
  );

  function updateEdit(code, field, value) {
    setEdits((current) => ({
      ...current,
      [code]: { ...current[code], [field]: value },
    }));
    setNotice('');
  }

  async function savePlan(code) {
    const plan = edits[code];
    if (!plan) return;
    try {
      setBusyCode(code);
      setError('');
      setNotice('');
      await adminApi.updateBillingPlan(code, {
        label: plan.label,
        durationValue: Number(plan.durationValue),
        durationUnit: plan.durationUnit,
        isTest: Boolean(plan.isTest),
        isActive: Boolean(plan.isActive),
        displayOrder: Number(plan.displayOrder),
        priceXof: Number(plan.priceXof || 0),
      });
      setNotice(`Formule « ${plan.label} » enregistrée.`);
      await load();
    } catch (failure) {
      setError(failure.message || 'Modification impossible.');
      if (failure.status === 401) onUnauthorized?.();
    } finally {
      setBusyCode('');
    }
  }

  async function createPlan(event) {
    event.preventDefault();
    try {
      setBusyCode('new');
      setError('');
      setNotice('');
      await adminApi.createBillingPlan({
        ...draft,
        durationValue: Number(draft.durationValue),
        displayOrder: Number(draft.displayOrder),
        priceXof: Number(draft.priceXof || 0),
      });
      setDraft(emptyDraft());
      setNotice('Nouvelle formule créée.');
      await load();
    } catch (failure) {
      setError(failure.message || 'Création impossible.');
      if (failure.status === 401) onUnauthorized?.();
    } finally {
      setBusyCode('');
    }
  }

  return (
    <div className="bo-stack">
      <div>
        <p className="bo-eyebrow">Abonnements</p>
        <h2>Formules Premium et durées de test</h2>
        <p className="bo-muted">
          Le backend calcule et impose les dates d’expiration. Les formules en minutes
          sont réservées aux tests et ne sont jamais exposées en production.
        </p>
      </div>

      {error && <div className="bo-alert bo-alert--error" role="alert">{error}</div>}
      {notice && <div className="bo-alert bo-alert--success" role="status">{notice}</div>}

      <section className="bo-panel">
        <div className="bo-panel__header">
          <div>
            <h3>État du bac à sable</h3>
            <p className="bo-muted">
              Paiements fictifs : {state.mockPaymentsEnabled ? 'activés' : 'désactivés'} ·
              {' '}durées courtes : {state.shortTestPlansEnabled ? 'activées' : 'désactivées'}.
            </p>
          </div>
          <span className="bo-kpi">
            {state.production ? 'Production' : 'Test / développement'}
          </span>
        </div>
        {!state.production && !state.shortTestPlansEnabled && (
          <div className="bo-alert">
            Pour afficher les plans en minutes aux candidats de test, configurez
            <code> ENABLE_MOCK_PAYMENTS=true </code> et
            <code> ENABLE_SHORT_TEST_SUBSCRIPTIONS=true </code>, puis redémarrez l’API.
          </div>
        )}
      </section>

      <PremiumAccessAudit onUnauthorized={onUnauthorized} />
      <PaymentHistory onUnauthorized={onUnauthorized} />

      <section className="bo-panel">
        <div className="bo-panel__header">
          <div>
            <h3>Formules configurées</h3>
            <p className="bo-muted">
              Une modification s’applique aux futurs paiements uniquement. Les intentions
              déjà créées conservent leur durée d’origine.
            </p>
          </div>
          <span className="bo-kpi">{orderedPlans.length} formule(s)</span>
        </div>

        {loading ? (
          <div className="bo-loading">Chargement…</div>
        ) : (
          <div className="bo-candidate-grid-wrap">
            <table className="bo-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Libellé</th>
                  <th>Durée</th>
                  <th>Prix FCFA</th>
                  <th>Type</th>
                  <th>Ordre</th>
                  <th>Active</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {orderedPlans.map((plan) => {
                  const edit = edits[plan.code] || plan;
                  return (
                    <tr key={plan.code}>
                      <td><code>{plan.code}</code></td>
                      <td>
                        <input className="bo-input" value={edit.label}
                          onChange={(event) => updateEdit(plan.code, 'label', event.target.value)} />
                      </td>
                      <td>
                        <div className="bo-inline-fields">
                          <input className="bo-input" type="number" min="1"
                            value={edit.durationValue}
                            onChange={(event) => updateEdit(
                              plan.code, 'durationValue', event.target.value
                            )} />
                          <select className="bo-input" value={edit.durationUnit}
                            onChange={(event) => updateEdit(
                              plan.code, 'durationUnit', event.target.value
                            )}>
                            <option value="minute">minute(s)</option>
                            <option value="month">mois</option>
                          </select>
                        </div>
                        <small className="bo-muted">{durationLabel(edit)}</small>
                      </td>
                      <td><input className="bo-input" type="number" min="0" max="10000000"
                        aria-label={`Prix FCFA ${plan.label}`} value={edit.priceXof ?? 0}
                        onChange={event => updateEdit(plan.code, 'priceXof', event.target.value)} /></td>
                      <td>
                        <select className="bo-input" value={edit.isTest ? 'test' : 'standard'}
                          onChange={(event) => updateEdit(
                            plan.code, 'isTest', event.target.value === 'test'
                          )}>
                          <option value="test">Test</option>
                          <option value="standard">Standard</option>
                        </select>
                      </td>
                      <td>
                        <input className="bo-input" type="number" min="0"
                          value={edit.displayOrder}
                          onChange={(event) => updateEdit(
                            plan.code, 'displayOrder', event.target.value
                          )} />
                      </td>
                      <td>
                        <input type="checkbox" checked={Boolean(edit.isActive)}
                          aria-label={`Activer ${plan.label}`}
                          onChange={(event) => updateEdit(
                            plan.code, 'isActive', event.target.checked
                          )} />
                      </td>
                      <td>
                        <button type="button" className="bo-button bo-button--primary"
                          disabled={busyCode === plan.code}
                          onClick={() => savePlan(plan.code)}>
                          {busyCode === plan.code ? 'Enregistrement…' : 'Enregistrer'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bo-panel">
        <div className="bo-panel__header">
          <div>
            <h3>Créer une formule</h3>
            <p className="bo-muted">
              Pour les essais d’expiration, utilisez un plan de type Test avec une durée en minutes.
            </p>
          </div>
        </div>
        <form className="bo-form" onSubmit={createPlan}>
          <div className="bo-form-grid">
            <label>
              Code
              <input className="bo-input" value={draft.code}
                onChange={(event) => setDraft((value) => ({ ...value, code: event.target.value }))} />
            </label>
            <label>
              Libellé
              <input className="bo-input" value={draft.label}
                onChange={(event) => setDraft((value) => ({ ...value, label: event.target.value }))} />
            </label>
            <label>
              Valeur
              <input className="bo-input" type="number" min="1" value={draft.durationValue}
                onChange={(event) => setDraft((value) => ({
                  ...value, durationValue: event.target.value,
                }))} />
            </label>
            <label>
              Unité
              <select className="bo-input" value={draft.durationUnit}
                onChange={(event) => setDraft((value) => ({
                  ...value, durationUnit: event.target.value,
                }))}>
                <option value="minute">minute(s)</option>
                <option value="month">mois</option>
              </select>
            </label>
            <label>
              Type
              <select className="bo-input" value={draft.isTest ? 'test' : 'standard'}
                onChange={(event) => setDraft((value) => ({
                  ...value, isTest: event.target.value === 'test',
                }))}>
                <option value="test">Test</option>
                <option value="standard">Standard</option>
              </select>
            </label>
            <label>Prix fictif en FCFA
              <input className="bo-input" type="number" min="0" max="10000000"
                value={draft.priceXof} onChange={event=>setDraft(v=>({...v,priceXof:event.target.value}))}/>
            </label>
            <label>
              Ordre
              <input className="bo-input" type="number" min="0" value={draft.displayOrder}
                onChange={(event) => setDraft((value) => ({
                  ...value, displayOrder: event.target.value,
                }))} />
            </label>
          </div>
          <button className="bo-button bo-button--primary" disabled={busyCode === 'new'}>
            {busyCode === 'new' ? 'Création…' : 'Créer la formule'}
          </button>
        </form>
      </section>
      <MobileMoneySandboxAdmin enabled={state.mockPaymentsEnabled && !state.production} />
    </div>
  );
}
